<template>
  <Layout class="mc-vehicle-media">
    <PageHeader :title="title" :sub-title="subTitle" />
    <div class="d-flex justify-content-end align-items-center gap-3 mc-vehicle-media__filter">
      <FormulateInput
        v-model="activeFilters.date"
        name="date"
        isShow="true"
        :placeholder="$t('global.object.period')"
        type="date-picker"
        datepickerType="date"
        valueType="YYYY-MM-DD"
        format="DD MMM YYYY"
        titleFormat="DD MMM YYYY"
        :confirm="true"
        :confirmText="$t('global.action.apply')"
        :clearable="false"
        :disabled-date="disabledRange"
        @close="closePicker"
        @input="handleChangePeriod($event)"
      />
      <ModalFilter
        :activeFilters="activeFilters"
        :vehicleObject="vehicleObject"
        @applyFilters="handleApplyFilters"
        @applyFullObjects="handleLocalFullObjects"
      />
    </div>

    <div class="border mc-vehicle-media__header-wrapper">
      <ModalFieldConfig
        :fields="fields"
        :defaultFields="defaultFields"
        class="mc-vehicle-media__header-config-wrapper"
        @applyConfigFields="applyConfigFields"
      />
    </div>

    <ListMedia ref="listMedia" :activeFilters="activeFilters" />
    <ModalMedia />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import ModalFilter from './components/ModalFilter.vue'
import { debounce, Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import datePickerLang from '@/helpers/date-picker-lang'
import ModalFieldConfig from './components/ModalFieldConfig.vue'
import ModalMedia from './components/ModalMedia.vue'
import ListMedia from './components/ListMedia.vue'
import moment from 'moment'
moment.locale('id')

export default {
  name: 'MediaIndex',
  page: {
    title: 'Media',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    ModalFilter,
    ModalFieldConfig,
    ListMedia,
    ModalMedia,
  },
  mixins: [Helper, MixPanel],
  data() {
    const defaultFields = [
      {
        key: 'mediaName',
        label: this.$t('media.fieldsManager.name'),
        isLocked: true,
        shown: true,
      },
      {
        key: 'date',
        label: this.$t('media.fieldsManager.date'),
        isLocked: true,
        shown: true,
      },
      {
        key: 'licensePlate',
        label: this.$t('media.fieldsManager.licensePlate'),
        isLocked: true,
        shown: true,
      },
      {
        key: 'driver',
        label: this.$t('media.fieldsManager.driver'),
        shown: true,
      },
      {
        key: 'vehicleType',
        label: this.$t('media.fieldsManager.vehicleType'),
        shown: false,
      },
      {
        key: 'vehicleGroup',
        label: this.$t('media.fieldsManager.vehicleGroup'),
        shown: false,
      },
      {
        key: 'address',
        label: this.$t('media.fieldsManager.address'),
        shown: true,
      },
    ]
    const fieldShow = JSON.parse(localStorage.getItem('mediaFields'))
    const fields = fieldShow ? fieldShow.column : defaultFields

    // get filters from localstorage
    let defaultValueDate = moment().format('YYYY-MM-DD'),
      defaultValueVehicleId = [],
      defaultValueMediaType = { all: false, photo: true, video: false }

    const filterCache = JSON.parse(localStorage.getItem('media-filters'))

    if (filterCache?.date) defaultValueDate = filterCache.date
    if (filterCache?.mediaType) defaultValueMediaType = filterCache?.mediaType
    if (filterCache?.vehicleId) defaultValueVehicleId = filterCache?.vehicleId

    // get full objects for filter vehicleId
    const fullObjectsFilter = JSON.parse(localStorage.getItem('media-full-objects'))
    let defaultValueFullObjectVehicleId = []
    if (fullObjectsFilter?.vehicleId) defaultValueFullObjectVehicleId = fullObjectsFilter?.vehicleId

    return {
      activeFilters: {
        date: defaultValueDate,
        vehicleId: defaultValueVehicleId,
        mediaType: { ...defaultValueMediaType },
      },
      vehicleObject: {
        vehicleId: defaultValueFullObjectVehicleId,
      },
      fieldShow,
      fields,
      defaultFields,
      pastDate: moment().subtract(3, 'months'),
      futureEnd: moment().add(3, 'months'),
      currentPastDate: moment().subtract(3, 'months'),
      currentFutureEnd: moment().add(3, 'months'),
    }
  },
  computed: {
    title() {
      return this.$t('global.object.media')
    },
    subTitle() {
      return this.$t('media.subtitle')
    },
    datePickerLang() {
      return datePickerLang()
    },
  },
  watch: {
    activeFilters: {
      deep: true,
      handler: debounce(function () {
        this.$refs.listMedia?.loadDataMedia(1)
      }, 200),
    },
  },

  created() {
    this.track('Open Trackvision Media')
  },
  mounted() {
    this.setFields()
  },

  methods: {
    async handleApplyFilters(values) {
      this.activeFilters.date = values.date
      this.activeFilters.vehicleId = values.vehicleId
      this.activeFilters.mediaType = values.mediaType

      localStorage.setItem(
        'media-filters',
        JSON.stringify({ vehicleId: values.vehicleId, mediaType: values.mediaType })
      )
    },
    setFields() {
      const fieldsStatus = {
        mediaName: this.fields.find((item) => item.key === 'mediaName').shown,
        date: this.fields.find((item) => item.key === 'date').shown,
        licensePlate: this.fields.find((item) => item.key === 'licensePlate').shown,
        driver: this.fields.find((item) => item.key === 'driver').shown,
        vehicleType: this.fields.find((item) => item.key === 'vehicleType').shown,
        vehicleGroup: this.fields.find((item) => item.key === 'vehicleGroup').shown,
        address: this.fields.find((item) => item.key === 'address').shown,
      }
      this.$store.commit('media/setFieldsStatus', fieldsStatus)
    },
    async applyConfigFields(fields) {
      this.fields = fields
      await this.$nextTick()
      this.setFields()
    },
    async handleChangePeriod(event) {
      this.activeFilters.date = event
    },
    disabledRange: function (date) {
      let limit = new Date()
      return (
        date <= this.pastDate ||
        date >= this.futureEnd ||
        date <= this.currentPastDate ||
        date >= this.currentFutureEnd ||
        date > limit
      )
    },
    closePicker() {
      this.currentPastDate = moment().subtract(3, 'months')
      this.currentFutureEnd = moment().add(3, 'months')
    },

    handleLocalFullObjects(value) {
      localStorage.setItem('media-full-objects', JSON.stringify(value))
    },
  },
}
</script>
